import { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { createMapboxMapSafely } from "../../../utils/mapboxInit";
import PropertyDynamicAttributes from "./PropertyDynamicAttributes";
import {
  LOCATION_DETAIL_DEFINITIONS,
  collectAttributeValues,
} from "../../../utils/propertyDetailAttributes";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || "";

const PropertyLocationCard = ({ property }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const location = property?.location || {};
  const canShowMap =
    MAPBOX_TOKEN &&
    location.mapLatitude != null &&
    location.mapLongitude != null &&
    !location.locationPending;

  const locationDetails = collectAttributeValues(property, LOCATION_DETAIL_DEFINITIONS);

  useEffect(() => {
    if (!canShowMap || !mapContainerRef.current) {
      return undefined;
    }

    const { map, error } = createMapboxMapSafely({
      container: mapContainerRef.current,
      accessToken: MAPBOX_TOKEN,
      style: "mapbox://styles/mapbox/light-v11",
      center: [location.mapLongitude, location.mapLatitude],
      zoom: location.approximateLocation ? 13 : 15,
      interactive: true,
      attributionControl: false,
    });

    if (!map) {
      return undefined;
    }

    mapRef.current = map;

    map.on("load", () => {
      if (location.approximateLocation) {
        map.addSource("property-area", {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [location.mapLongitude, location.mapLatitude],
            },
          },
        });
        map.addLayer({
          id: "property-area-circle",
          type: "circle",
          source: "property-area",
          paint: {
            "circle-radius": 40,
            "circle-color": "#2563eb",
            "circle-opacity": 0.15,
            "circle-stroke-width": 2,
            "circle-stroke-color": "#2563eb",
          },
        });
      } else {
        new mapboxgl.Marker({ color: "#2563eb" })
          .setLngLat([location.mapLongitude, location.mapLatitude])
          .addTo(map);
      }
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [
    canShowMap,
    location.approximateLocation,
    location.mapLatitude,
    location.mapLongitude,
  ]);

  return (
    <section className="property-detail-section" aria-label="Location">
      <h2 className="property-detail-section__title">Location</h2>

      {canShowMap ? (
        <div
          ref={mapContainerRef}
          className="property-detail-location__map"
          role="application"
          aria-label={
            location.approximateLocation
              ? "Approximate property area map"
              : "Property location map"
          }
        />
      ) : (
        <p className="property-detail-empty">Location not mapped yet.</p>
      )}

      {location.approximateLocation ? (
        <p className="property-detail-location__note">
          Map shows an approximate area. Exact coordinates are not displayed for privacy.
        </p>
      ) : null}

      <PropertyDynamicAttributes items={locationDetails} columns={1} />
    </section>
  );
};

export default PropertyLocationCard;
